import { defineMessages } from 'react-intl.macro';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useSelector } from '@tmapy/redux';
import { Autocomplete, FormItem, FormRow, Input } from '@tmapy/style-guide';

import { getAddress, getCity, IAddressItem } from '../../utils/ruian';
import { useWhisperer } from '../../hooks';
import type { FormItemServerErrors } from '../../hooks/useFormItemServerErrors';
import { resListOperation } from '../../openapi/operations/resList';

export interface IResItem {
  name: string | null;
  ico: string | null;
  birth_number: string | null;
  city: string | null;
  street: string | null;
  postal_code: string | null;
}

const getAdressFromRuian = (ruianAdress: IAddressItem): IResItem => {
  return {
    name: null,
    ico: null,
    birth_number: null,
    city: getCity(ruianAdress),
    street: getAddress(ruianAdress),
    postal_code: ruianAdress.postal_code || '',
  };
};

const msg = defineMessages({
  name: {
    id: 'reswhisperer.name.label',
    defaultMessage: 'Jméno',
  },
  ico: {
    id: 'reswhisperer.ico.label',
    defaultMessage: 'IČO',
  },
  icoRequiredLabel: {
    id: 'reswhisperer.ico.requiredLabel',
    defaultMessage: 'povinné, pokud není vyplněno RČ',
  },
  birth_number: {
    id: 'reswhisperer.birth_number.label',
    defaultMessage: 'RČ',
  },
  birth_numberRequiredLabel: {
    id: 'reswhisperer.birth_number.requiredLabel',
    defaultMessage: 'povinné, pokud není vyplněno IČO',
  },
  street: {
    id: 'addressautocomplete.street.label',
    defaultMessage: 'Ulice, č. p.',
  },
  city: {
    id: 'addressautocomplete.city.label',
    defaultMessage: 'Obec',
  },
  psc: {
    id: 'addressautocomplete.psc.label',
    defaultMessage: 'PSČ',
  },
});

export const ResWhisperer: React.FC<{
  serverErrors: FormItemServerErrors | null;
  streetIdent: string;
  streetValue: string;
  cityIdent: string;
  cityValue: string;
  postalCodeIdent: string;
  postalCodeValue: string;
  nameIdent: string;
  nameValue: string;
  nameErrorMessage: string;
  icoIdent: string;
  icoValue: string;
  icoErrorMessage: string;
  birthNumberIdent: string;
  birthNumberValue: string;
  validate: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  formItemChangeCallback(): void;
  onChange(e: React.MouseEvent | React.ChangeEvent, data: { ident: string; value: string }[]): void;
}> = React.memo(
  ({
    serverErrors,
    streetIdent,
    streetValue,
    cityIdent,
    cityValue,
    postalCodeIdent,
    postalCodeValue,
    nameIdent,
    nameValue,
    nameErrorMessage,
    icoIdent,
    icoValue,
    icoErrorMessage,
    birthNumberIdent,
    birthNumberValue,
    validate,
    isDisabled,
    isRequired,
    formItemChangeCallback,
    onChange,
  }) => {
    const intl = useIntl();
    const [nameItems, setNameItems] = useState<IResItem[]>([]);
    const [icoItems, setIcoItems] = useState<IResItem[]>([]);
    const [birthNumberItems, setBirthNumberItems] = useState<IResItem[]>([]);
    const [addressItems, setAddressItems] = useState<IAddressItem[]>([]);
    const [name, setName] = useState<string>(nameValue);
    const [ico, setIco] = useState<string>(icoValue);
    const [birth_number, setBirth_number] = useState<string>(birthNumberValue);
    const [street, setStreet] = useState<string>(streetValue);
    const [city, setCity] = useState<string>(cityValue);
    const [postal_code, setPostalCode] = useState<string>(postalCodeValue);
    const app = useSelector((state) => state.app);
    const isNameInvalid = useRef(false);
    const isIcoInvalid = useRef(false);
    const isBirthNumberInvalid = useRef(false);

    const [outputData, setOutputData] = useState([
      { ident: streetIdent, value: street },
      { ident: cityIdent, value: city },
      { ident: postalCodeIdent, value: postal_code },
      { ident: nameIdent, value: name },
      { ident: icoIdent, value: ico },
      { ident: birthNumberIdent, value: birth_number },
    ]);

    const updateOutputData = useCallback(
      (data: { ident: string; value: string }) => {
        const updatedData = outputData.map((item) => {
          if (data.ident === item.ident) {
            item.value = data.value;
          }
          return item;
        });

        setOutputData(updatedData);
      },
      [setOutputData, outputData],
    );

    const fetchNameData = useWhisperer({
      getUrl: (name__icontains: string) =>
        `${app.config.server}${resListOperation({ name__icontains }).path}`,
      setItems: (data: any) => {
        setNameItems(data?.results?.length ? data.results : []);
      },
      sendAuth: true,
    });

    const fetchIcoData = useWhisperer({
      getUrl: (ico__icontains: string) =>
        `${app.config.server}${resListOperation({ ico__icontains }).path}`,
      setItems: (data: any) => {
        setIcoItems(data?.results?.length ? data.results : []);
      },
      sendAuth: true,
    });

    const fetchBirthNumberData = useWhisperer({
      getUrl: (birth_number__icontains: string) =>
        `${app.config.server}${resListOperation({ birth_number__icontains }).path}`,

      setItems: (data: any) => {
        setBirthNumberItems(data?.results?.length ? data.results : []);
      },
      sendAuth: true,
    });

    const fetchRuianData = useWhisperer({
      getUrl: (value: string) => `${app.services.suggestAddress}&singleLine=${value}`,
      setItems: (data: any) => {
        let addressItems: IAddressItem[] = [];
        if (data?.candidates?.length) {
          addressItems = data.candidates.map(
            (item: any) =>
              ({
                address: item.address,
                city: item.attributes.extra.obecNazev,
                cityPart: item.attributes.extra.castObceNazev,
                street: item.attributes.extra.uliceNazev,
                postal_code: item.attributes.extra.psc,
                houseNumber: item.attributes.extra.cisloDomovni,
                orientationNumber: item.attributes.extra.cisloOrientacni,
              }) as IAddressItem,
          );
        }
        setAddressItems(addressItems);
      },
      sendAuth: false,
    });

    const handleSuggestionClick = (
      e: React.MouseEvent,
      eventData: { item: IResItem | IAddressItem; name: string },
    ) => {
      const item =
        eventData.name === streetIdent ? getAdressFromRuian(eventData.item) : eventData.item;

      updateOutputData({ ident: streetIdent, value: item.street || '' });
      updateOutputData({ ident: cityIdent, value: item.city || '' });
      updateOutputData({ ident: postalCodeIdent, value: item.postal_code || '' });

      if (eventData.name !== streetIdent) {
        updateOutputData({ ident: nameIdent, value: (item as IResItem).name || '' });
        updateOutputData({ ident: icoIdent, value: (item as IResItem).ico || '' });
        birthNumberIdent &&
          updateOutputData({
            ident: birthNumberIdent,
            value: (item as IResItem).birth_number || '',
          });
      }

      setCity(item.city || '');
      setStreet(item.street || '');
      setPostalCode(item.postal_code || '');
      if (eventData.name !== streetIdent) {
        setName((item as IResItem).name || '');
        setIco((item as IResItem).ico || '');
        setBirth_number((item as IResItem).birth_number || '');
      }

      onChange(e, outputData);
    };

    const handleAutocompleteChange = (
      e: React.ChangeEvent,
      eventData: { name: string; value: string },
    ) => {
      switch (eventData.name) {
        case nameIdent:
          fetchNameData(eventData.value);
          break;
        case icoIdent:
          fetchIcoData(eventData.value);
          break;
        case birthNumberIdent:
          fetchBirthNumberData(eventData.value);
          break;
        case streetIdent:
          fetchRuianData(eventData.value);
          break;
      }
      updateOutputData({ ident: eventData.name, value: eventData.value });

      switch (eventData.name) {
        case nameIdent:
          setName(eventData.value);
          break;
        case icoIdent:
          setIco(eventData.value);
          break;
        case birthNumberIdent:
          setBirth_number(eventData.value);
          break;
        case streetIdent:
          setStreet(eventData.value);
          break;
      }
      onChange(e, outputData);
    };

    const handleNameChange = (e: React.ChangeEvent, eventData: { name: string; value: string }) => {
      formItemChangeCallback();
      handleAutocompleteChange(e, eventData);
    };

    const handleIcoChange = (e: React.ChangeEvent, eventData: { name: string; value: string }) => {
      formItemChangeCallback();
      handleAutocompleteChange(e, eventData);
    };

    const handleBirthNumberChange = (
      e: React.ChangeEvent,
      eventData: { name: string; value: string },
    ) => {
      formItemChangeCallback();
      handleAutocompleteChange(e, eventData);
    };

    const handleInputChange = (
      e: React.ChangeEvent | React.MouseEvent,
      eventData: { name: string; value: string },
    ) => {
      updateOutputData({ ident: eventData.name, value: eventData.value });

      switch (eventData.name) {
        case cityIdent:
          setCity(eventData.value);
          break;
        case postalCodeIdent:
          setPostalCode(eventData.value);
          break;
      }
      onChange(e, outputData);
    };

    const renderNameItem = (item: { name: string }) => <>{item.name}</>;
    const renderIcoItem = (item: { ico: string; name: string }) => (
      <>
        {item.ico} - {item.name}
      </>
    );
    const renderBirthNumberItem = (item: { birth_number: string; name: string }) => (
      <>
        {item.birth_number} - {item.name}
      </>
    );
    const renderAddressItem = (item: { address: string }) => <>{item.address}</>;

    useEffect(
      () => () => {
        fetchNameData.cancel();
        fetchIcoData.cancel();
        fetchRuianData.cancel();
      },
      [fetchNameData, fetchIcoData, fetchRuianData],
    );

    if (validate || isNameInvalid.current) {
      isNameInvalid.current = !name;
    }

    if (validate || isIcoInvalid.current || isBirthNumberInvalid.current) {
      isIcoInvalid.current = !birth_number && !ico;
      isBirthNumberInvalid.current = !birth_number && !ico;
    }

    return (
      <>
        <FormRow>
          <FormItem
            label={intl.formatMessage(msg.name)}
            isRequired={isRequired}
            errorMessage={isNameInvalid.current ? nameErrorMessage : null}
            {...serverErrors?.[nameIdent]}
          >
            <Autocomplete
              isDisabled={isDisabled}
              name={nameIdent}
              value={name}
              items={nameItems}
              isRequired={isRequired}
              isInvalid={isNameInvalid.current}
              renderItem={renderNameItem}
              onChange={handleNameChange}
              onItemClick={handleSuggestionClick}
              {...serverErrors?.[nameIdent]}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          {icoIdent && (
            <FormItem
              label={intl.formatMessage(msg.ico)}
              isRequired={isRequired}
              errorMessage={isIcoInvalid.current ? icoErrorMessage : null}
              requiredLabel={intl.formatMessage(msg.icoRequiredLabel)}
              {...serverErrors?.[icoIdent]}
            >
              <Autocomplete
                isDisabled={isDisabled}
                name={icoIdent}
                value={ico}
                items={icoItems}
                isRequired={isRequired}
                isInvalid={isIcoInvalid.current}
                renderItem={renderIcoItem}
                onChange={handleIcoChange}
                onItemClick={handleSuggestionClick}
                {...serverErrors?.[icoIdent]}
              />
            </FormItem>
          )}
          {birthNumberIdent && (
            <FormItem
              label={intl.formatMessage(msg.birth_number)}
              isRequired={isRequired}
              requiredLabel={intl.formatMessage(msg.birth_numberRequiredLabel)}
              {...serverErrors?.[birthNumberIdent]}
            >
              <Autocomplete
                isDisabled={isDisabled}
                name={birthNumberIdent}
                value={birth_number}
                items={birthNumberItems}
                isRequired={isRequired}
                isInvalid={isBirthNumberInvalid.current}
                renderItem={renderBirthNumberItem}
                onChange={handleBirthNumberChange}
                onItemClick={handleSuggestionClick}
                {...serverErrors?.[birthNumberIdent]}
              />
            </FormItem>
          )}
        </FormRow>
        <FormRow>
          {streetIdent && (
            <FormItem label={intl.formatMessage(msg.street)} {...serverErrors?.[streetIdent]}>
              <Autocomplete
                isDisabled={isDisabled}
                name={streetIdent}
                value={street}
                items={addressItems}
                renderItem={renderAddressItem}
                onChange={handleAutocompleteChange}
                onItemClick={handleSuggestionClick}
                {...serverErrors?.[streetIdent]}
              />
            </FormItem>
          )}
          {cityIdent && (
            <FormItem label={intl.formatMessage(msg.city)} {...serverErrors?.[cityIdent]}>
              <Input
                isDisabled={isDisabled}
                name={cityIdent}
                autoComplete='city'
                value={city}
                onChange={handleInputChange}
                {...serverErrors?.[cityIdent]}
              />
            </FormItem>
          )}
          {postalCodeIdent && (
            <FormItem
              label={intl.formatMessage(msg.psc)}
              characterCount={6}
              {...serverErrors?.[postalCodeIdent]}
            >
              <Input
                isDisabled={isDisabled}
                name={postalCodeIdent}
                autoComplete='zip-code'
                value={postal_code}
                onChange={handleInputChange}
                {...serverErrors?.[postalCodeIdent]}
              />
            </FormItem>
          )}
        </FormRow>
      </>
    );
  },
);
